/**
 * GDSEditModal.js - Fixed Version
 * Handles edit/customize modal functionality for product cards
 * This version ensures exact behavior match with the original implementation
 */

export class GDSEditModal {
    constructor(modalManager) {
        this.modalManager = modalManager;
        this.builder = modalManager.builder;
        this.cardManager = modalManager.builder.cardManager;
        this.dataService = this.cardManager.dataService;
        this.renderer = this.cardManager.renderer;
    }

    open(modal) {
        if (!modal) return;

        // Store current scroll position BEFORE making any changes
        const currentScrollY = window.scrollY;

        // Setup modal close listeners first (matching original)
        const closeButton = modal.querySelector('.modal-close');
        if (closeButton && !closeButton.hasAttribute('data-gds-card-close-listener')) {
            closeButton.addEventListener('click', (e) => {
                e.stopPropagation();
                this.close(modal);
            });
            closeButton.setAttribute('data-gds-card-close-listener', 'true');
        }

        if (!modal.hasAttribute('data-gds-card-backdrop-listener')) {
            modal.addEventListener('click', (e) => {
                if (e.target === modal || e.target.classList.contains('modal-backdrop')) {
                    this.close(modal);
                }
            });
            modal.setAttribute('data-gds-card-backdrop-listener', 'true');
        }

        // Show modal first
        modal.classList.remove('hidden');
        document.body.classList.add('modal-open');

        // Prevent scroll without position fixed jump (matching original)
        document.body.style.overflow = 'hidden';
        modal._scrollPosition = currentScrollY;
        document.body.style.overscrollBehavior = 'none';

        // For iOS Safari
        if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
            document.body.style.position = 'fixed';
            document.body.style.top = `-${currentScrollY}px`;
            document.body.style.width = '100%';
        }

        // Setup modal scroll prevention
        this.setupModalScrollPrevention(modal);

        // Animate modal based on device
        const backdrop = modal.querySelector('.modal-backdrop');
        const isMobile = window.innerWidth < 768;

        if (isMobile) {
            this.setupMobileScrollPrevention(modal);
        }

        if (isMobile) {
            const mobileContent = modal.querySelector('.mobile-modal-content');
            if (mobileContent) {
                this.animateMobileModal(modal, backdrop);
                this.setupMobileModalSwipe(modal, mobileContent);
            }
        } else {
            const contentContainer = modal.querySelector('.md\\:flex.items-center.justify-center');
            const content = contentContainer?.querySelector('.relative.bg-white');
            if (content && backdrop) {
                this.animateDesktopModal(modal, backdrop);
            }
        }

        // Setup card-specific logic
        const modalId = modal.id;
        const activeCard = this.builder.contentContainer.querySelector(`.product-card[data-modal-id="${modalId}"]`);
        if (activeCard) {
            this.builder.contentContainer.querySelectorAll('.product-card.active-modal').forEach(card => {
                card.classList.remove('active-modal');
            });
            activeCard.classList.add('active-modal');

            const currentSelectionsAttr = activeCard.getAttribute('data-selections');
            let currentSelections = {};
            if (currentSelectionsAttr) {
                try {
                    currentSelections = JSON.parse(currentSelectionsAttr);
                } catch (e) {
                    console.error('Error parsing selections from card:', e);
                }
            } else {
                const cardData = this.dataService.getCurrentCardData(activeCard);
                if (cardData && cardData.default) {
                    currentSelections = this.determineDefaultSelections(cardData);
                }
            }
            modal._tempSelections = JSON.parse(JSON.stringify(currentSelections));
            this.populateOptions(modal, activeCard);

            // Re-setup options scroll prevention after content is populated
            if (isMobile) {
                setTimeout(() => {
                    const optionsContainer = modal.querySelector('.mobile-modal-content .modal-options-container, .mobile-modal-content .space-y-4');
                    if (optionsContainer) {
                        this.setupOptionsScrollPrevention(optionsContainer);
                    }
                }, 100);
            }
        }

        // Ensure global escape listener
        if (this.modalManager._ensureGlobalEscapeListener) {
            this.modalManager._ensureGlobalEscapeListener();
        }
    }

    close(modal, confirmed = false) {
        if (!modal) return;

        // Clear temp selections if not confirmed
        if (!confirmed && modal._tempSelections) {
            delete modal._tempSelections;
        }

        // Get elements for animation
        const mobileContent = modal.querySelector('.mobile-modal-content');
        const backdrop = modal.querySelector('.modal-backdrop');
        const contentContainer = modal.querySelector('.md\\:flex.items-center.justify-center');
        const content = contentContainer?.querySelector('.relative.bg-white');

        // Remove the modal open class from the body
        document.body.classList.remove('modal-open');

        // Different handling for mobile vs desktop
        const isMobile = window.innerWidth < 768;

        if (isMobile && mobileContent) {
            this.animateModalClose(modal, mobileContent, backdrop, true);
        } else if (!isMobile && content && backdrop) {
            this.animateModalClose(modal, content, backdrop, false);
        } else {
            modal.classList.add('hidden');
            this.restorePageScroll(modal);
        }

        // Restore page scroll properly after animation
        setTimeout(() => {
            this.restorePageScroll(modal);

            if (mobileContent) {
                mobileContent.style.transform = '';
                mobileContent.style.transition = 'transform 0.3s ease-out';
            }
        }, 300);
    }

    populateOptions(modal, card) {
        const cardData = this.dataService.getCurrentCardData(card);
        if (!cardData) {
            console.error('Card data missing for modal population.');
            const desktopOptionsContainer = modal.querySelector('.md\\:flex .modal-options-container');
            const mobileOptionsContainer = modal.querySelector('.mobile-modal-content .modal-options-container');

            const errorMessage = '<p class="text-sm text-gray-500 italic">Error: Configuration data missing.</p>';
            if (desktopOptionsContainer) {
                desktopOptionsContainer.innerHTML = errorMessage;
            }
            if (mobileOptionsContainer) {
                mobileOptionsContainer.innerHTML = errorMessage;
            }
            return;
        }

        // Use temporary selections for the modal
        let currentSelections;

        if (modal._tempSelections) {
            currentSelections = modal._tempSelections;
        } else {
            const selectionsAttr = card.getAttribute('data-selections');
            if (selectionsAttr) {
                try {
                    currentSelections = JSON.parse(selectionsAttr);
                } catch (e) {
                    console.error('Error parsing existing selections:', e);
                    currentSelections = {};
                }
            } else if (cardData.default) {
                currentSelections = this.determineDefaultSelections(cardData);
            } else {
                currentSelections = {};
            }

            modal._tempSelections = JSON.parse(JSON.stringify(currentSelections));
        }

        // Find both desktop and mobile option containers
        const desktopOptionsContainer = modal.querySelector('.md\\:flex .modal-options-container');
        const mobileOptionsContainer = modal.querySelector('.mobile-modal-content .modal-options-container');

        if (!desktopOptionsContainer && !mobileOptionsContainer) {
            console.error('Could not find any options container in modal.');
            return;
        }

        // Clear previous content
        if (desktopOptionsContainer) desktopOptionsContainer.innerHTML = '';
        if (mobileOptionsContainer) mobileOptionsContainer.innerHTML = '';

        // Verify we have both dropdowns and options
        if (!cardData.dropdowns || !cardData.options) {
            const noOptionsMessage = `<p class="text-sm text-gray-500 italic">No customization options available.</p>`;
            if (desktopOptionsContainer) desktopOptionsContainer.innerHTML = noOptionsMessage;
            if (mobileOptionsContainer) mobileOptionsContainer.innerHTML = noOptionsMessage;
            console.warn('Missing dropdowns or options in card data for card:', card.getAttribute('data-card-title'));
            return;
        }

        let optionsHTML = '';
        let hasVisibleOptions = false;

        try {
            // Process each dropdown field in order
            cardData.dropdowns.forEach((dropdownObj) => {
                const fieldName = Object.keys(dropdownObj)[0];
                const fieldLabel = dropdownObj[fieldName];

                // Get all possible values for this field
                const allPossibleValues = this.getAllPossibleValuesForField(cardData.options, fieldName, cardData.dropdowns);

                // Get currently valid values based on current selections
                const { validValues } = this.getPossibleValuesForField(cardData, fieldName, modal._tempSelections);

                let displayInfo;

                // Determine display type
                const hasYes = allPossibleValues.includes('Yes');
                const hasNA = allPossibleValues.includes('N/A');
                const isTrulyYesNACaseForField = hasYes && hasNA && allPossibleValues.length === 2;

                if (isTrulyYesNACaseForField) {
                    displayInfo = { displayType: 'Checkbox' };
                } else if (allPossibleValues && allPossibleValues.length > 0) {
                    displayInfo = this.getOptionDisplayType(fieldName, allPossibleValues[0], modal._tempSelections[fieldName] === allPossibleValues[0]);
                } else {
                    displayInfo = this.getOptionDisplayType(fieldName, undefined);
                }

                // Render the section for this field
                const sectionHTML = this.renderOptionSection(
                    fieldLabel,
                    fieldName,
                    allPossibleValues,
                    validValues,
                    modal._tempSelections[fieldName] || '',
                    displayInfo
                );

                if (sectionHTML) {
                    optionsHTML += sectionHTML;
                    const isSingleNA = allPossibleValues.length === 1 && allPossibleValues[0] === 'N/A';
                    if (!isSingleNA) {
                        hasVisibleOptions = true;
                    }

                    // Auto-select N/A if it's the only option
                    if (isSingleNA && !modal._tempSelections[fieldName]) {
                        modal._tempSelections[fieldName] = 'N/A';
                    }
                }
            });
        } catch (error) {
            console.error('Error generating options HTML:', error);
            optionsHTML = `<p class="text-sm text-red-500 italic">Error loading customization options: ${error.message}</p>`;
            hasVisibleOptions = true;
        }

        // Update both containers with the same HTML content
        if (!optionsHTML || !hasVisibleOptions) {
            optionsHTML = `<p class="text-sm text-gray-500 italic">No customization options available.</p>`;
        }

        if (desktopOptionsContainer) desktopOptionsContainer.innerHTML = optionsHTML;
        if (mobileOptionsContainer) mobileOptionsContainer.innerHTML = optionsHTML;

        // Set up event listeners
        this.setupOptionEventListeners(modal, card, modal._tempSelections);

        // Ensure hidden N/A fields are selected
        this.ensureHiddenNAFieldsSelected(modal, modal._tempSelections);

        // Setup field visibility
        this.setupFieldVisibility();
    }

    setupOptionEventListeners(modal, card, currentSelections) {
        const desktopOptionsContainer = modal.querySelector('.md\\:flex .space-y-4, .md\\:flex .modal-options-container');
        const mobileOptionsContainer = modal.querySelector('.mobile-modal-content .space-y-4, .mobile-modal-content .modal-options-container');

        if (desktopOptionsContainer) {
            this.setupContainerEventListeners(desktopOptionsContainer, modal, card, currentSelections);
        }

        if (mobileOptionsContainer) {
            this.setupContainerEventListeners(mobileOptionsContainer, modal, card, currentSelections);
        }

        this.setupModalSaveButtons(modal, card, currentSelections);
    }

    setupContainerEventListeners(container, modal, card, currentSelections) {
        if (!container) return;

        // Use event delegation on the container
        container.addEventListener('click', (e) => {
            // Start from the target and walk up to find an element with data-value
            let element = e.target;
            let optionElement = null;

            // Walk up the DOM tree until we find an element with data-value or reach the container
            while (element && element !== container) {
                if (element.hasAttribute && element.hasAttribute('data-value')) {
                    // Found an element with data-value
                    optionElement = element;
                    break;
                }
                element = element.parentElement;
            }

            if (!optionElement) {
                return; // No option element found in the click path
            }

            // Check if this is a button, icon option, or color option
            const isButton = optionElement.tagName === 'BUTTON';
            const isIconOption = optionElement.classList.contains('icon-option');
            const isColorOption = optionElement.querySelector('.w-6.h-6.rounded-full') !== null;

            // Only process if it's one of our option types
            if (isButton || isIconOption || isColorOption) {
                e.preventDefault();
                e.stopPropagation();

                // Find the field container
                const fieldContainer = optionElement.closest('.option-container[data-field]');
                if (!fieldContainer) {
                    console.error('Could not find field container for clicked option');
                    return;
                }

                const field = fieldContainer.dataset.field;
                const value = optionElement.dataset.value;

                // console.log(`Option clicked: field="${field}", value="${value}"`);

                // Call handleOptionSelection
                this.handleOptionSelection(field, value, currentSelections, modal, card);
            }
        }, true); // Use capture phase to ensure we get the event early

        // Handle dropdowns
        container.addEventListener('change', (e) => {
            if (e.target.tagName === 'SELECT' && e.target.hasAttribute('data-field')) {
                const field = e.target.dataset.field;
                const value = e.target.value;
                // console.log(`Dropdown changed: field="${field}", value="${value}"`);
                this.handleOptionSelection(field, value, currentSelections, modal, card);
            }
        });

        // Handle checkboxes
        container.addEventListener('change', (e) => {
            if (e.target.type === 'checkbox' && e.target.hasAttribute('data-value')) {
                const fieldContainer = e.target.closest('.option-container[data-field]');
                if (!fieldContainer) return;

                const field = fieldContainer.dataset.field;
                let value;

                if (e.target.checked) {
                    value = e.target.dataset.value;
                } else {
                    const isYesNa = e.target.dataset.isYesNa === 'true';
                    value = isYesNa ? e.target.dataset.alternateValue : undefined;
                }

                // console.log(`Checkbox changed: field="${field}", value="${value}"`);
                this.handleOptionSelection(field, value, currentSelections, modal, card);
            }
        });
    }

    setupModalSaveButtons(modal, card, currentSelections) {
        const desktopSaveButton = modal.querySelector('.md\\:flex .modal-save-btn');
        if (desktopSaveButton) {
            const newDesktopSaveButton = desktopSaveButton.cloneNode(true);
            desktopSaveButton.parentNode.replaceChild(newDesktopSaveButton, desktopSaveButton);

            newDesktopSaveButton.addEventListener('click', (e) => {
                e.stopPropagation();
                this.handleSave(modal, card, currentSelections);
            });
        }

        const mobileSaveButton = modal.querySelector('.mobile-modal-content .modal-save-btn');
        if (mobileSaveButton) {
            const newMobileSaveButton = mobileSaveButton.cloneNode(true);
            mobileSaveButton.parentNode.replaceChild(newMobileSaveButton, mobileSaveButton);

            newMobileSaveButton.addEventListener('click', (e) => {
                e.stopPropagation();
                this.handleSave(modal, card, currentSelections);
            });
        }
    }

    handleOptionSelection(field, value, currentSelections, modal, card) {
        // console.log(`Option selection changed: ${field} = ${value !== undefined ? value : 'CLEARED'}`);

        const previousSelections = JSON.parse(JSON.stringify(currentSelections));
        const previousValueForField = previousSelections[field];
        const cardData = this.dataService.getCurrentCardData(card);

        if (!cardData || !cardData.dropdowns || !cardData.options) {
            console.warn('No card data found for option selection handling on card:', card.getAttribute('data-card-title'));
            return;
        }

        const allFields = cardData.dropdowns.map(dropdown => Object.keys(dropdown)[0]);
        const primaryField = allFields[0];
        const fieldIndex = allFields.indexOf(field);

        if (fieldIndex === -1) {
            console.warn(`Field ${field} not found in dropdowns array for card:`, card.getAttribute('data-card-title'));
            return;
        }

        // Update currentSelections with the user's direct change
        if (value === undefined) {
            const fieldContainer = modal.querySelector(`.option-container[data-field="${field}"]`);
            let wasHandledAsCheckbox = false;

            if (fieldContainer) {
                const yesNaCheckbox = fieldContainer.querySelector(`input[type="checkbox"][data-is-yes-na="true"][data-alternate-value]`);
                if (yesNaCheckbox) {
                    const alternateValue = yesNaCheckbox.getAttribute('data-alternate-value');
                    currentSelections[field] = alternateValue;
                    wasHandledAsCheckbox = true;
                } else {
                    const generalCheckbox = fieldContainer.querySelector(`input[type="checkbox"]`);
                    if (generalCheckbox) {
                        delete currentSelections[field];
                        wasHandledAsCheckbox = true;
                    }
                }
            }

            if (!wasHandledAsCheckbox) {
                delete currentSelections[field];
            }
        } else {
            currentSelections[field] = value;
        }

        // Clear dependent fields if the value actually changed
        const valueActuallyChanged = (previousValueForField !== currentSelections[field]);
        if (valueActuallyChanged) {
            for (let i = fieldIndex + 1; i < allFields.length; i++) {
                const dependentField = allFields[i];
                if (currentSelections.hasOwnProperty(dependentField)) {
                    delete currentSelections[dependentField];
                }
            }
        }

        let pathFound = false;
        let finalSelections = {};
        let propagationNeeded = false;
        let propagationChanges = {};

        // Create a fresh selections object with ONLY the fields up to and including the changed field
        let testSelections = {};

        // Copy all fields before the changed field
        for (let i = 0; i < fieldIndex; i++) {
            if (currentSelections[allFields[i]] !== undefined) {
                testSelections[allFields[i]] = currentSelections[allFields[i]];
            }
        }

        // Add the changed field with the new value
        if (value !== undefined) {
            testSelections[field] = value;
        }

        // Attempt 1: Try to find a complete path with the user's selection
        if (value !== undefined) {
            // console.log(`[Attempt 1] Trying to find path with ${field}=${value}`);
            let attempt1Selections = JSON.parse(JSON.stringify(testSelections));
            pathFound = this.dataService.findCompleteValidPath(cardData, field, value, attempt1Selections, allFields, previousSelections);

            if (pathFound) {
                // console.log(`[Attempt 1] Success! Found valid path`);
                finalSelections = attempt1Selections;
            } else {
                // console.log(`[Attempt 1] Failed - ${field}=${value} is not compatible with current selections`);
            }
        } else {
            finalSelections = JSON.parse(JSON.stringify(currentSelections));
            pathFound = true;
        }

        // Attempt 2: Smart Adjust - Try adjusting intermediate fields
        if (!pathFound && value !== undefined) {
            // console.log(`[SmartAdjust] Attempting to find compatible path by adjusting intermediate fields`);
            let newPathCandidate = this.dataService.findPathByAdjustingIntermediateFields(
                cardData,
                previousSelections,
                field,
                value,
                allFields
            );

            if (newPathCandidate) {
                finalSelections = newPathCandidate;
                pathFound = true;
            }
        }

        // Attempt 3: Try alternative primary field values
        if (!pathFound && value !== undefined) {
            // console.log(`[Fallback] Searching for alternative ${primaryField} supporting ${field}=${value}`);
            const originalPrimaryFieldValue = previousSelections[primaryField];
            const allPrimaryFieldValues = Object.keys(cardData.options);

            for (const primaryOptionValue of allPrimaryFieldValues) {
                if (primaryOptionValue === originalPrimaryFieldValue) continue;

                let attempt3Selections = {
                    [primaryField]: primaryOptionValue,
                    [field]: value
                };

                if (this.dataService.findCompleteValidPath(cardData, field, value, attempt3Selections, allFields, {})) {
                    // console.log(`[Fallback] Found working alternative: ${primaryField}=${primaryOptionValue}`);
                    finalSelections = attempt3Selections;
                    pathFound = true;

                    // Check if this primary field change requires propagation
                    if (this.isPropagatingField(primaryField)) {
                        propagationNeeded = true;
                        propagationChanges[primaryField] = primaryOptionValue;
                    }
                    break;
                }
            }
        }

        // Attempt 4: Intelligent nearest alternative search
        if (!pathFound && value !== undefined) {
            // console.log(`[IntelligentFallback] Searching for nearest valid alternative`);
            const nearestAlternative = this.findNearestValidAlternative(
                cardData,
                field,
                value,
                previousSelections,
                allFields
            );

            if (nearestAlternative) {
                finalSelections = nearestAlternative.selections;
                pathFound = true;

                // Check for propagation needs
                for (const [changedField, newValue] of Object.entries(nearestAlternative.changes)) {
                    if (this.isPropagatingField(changedField) && previousSelections[changedField] !== newValue) {
                        propagationNeeded = true;
                        propagationChanges[changedField] = newValue;
                    }
                }
            }
        }

        // Attempt 5: Default to first viable option
        if (!pathFound && value !== undefined) {
            // console.log(`[DefaultFallback] Defaulting to first viable option`);
            const firstViable = this.findFirstViableOption(cardData, allFields);

            if (firstViable) {
                finalSelections = firstViable;
                pathFound = true;

                // Check for propagation needs
                for (const propField of this.getPropagatingFields()) {
                    if (previousSelections[propField] !== firstViable[propField]) {
                        propagationNeeded = true;
                        propagationChanges[propField] = firstViable[propField];
                    }
                }
            }
        }

        // Finalize currentSelections
        if (pathFound) {
            Object.keys(currentSelections).forEach(key => delete currentSelections[key]);
            Object.assign(currentSelections, finalSelections);
            // console.log('[Final] Successfully found valid path:', JSON.stringify(currentSelections));
        } else if (value !== undefined) {
            // Keep minimal conflicting selection as last resort
            console.warn(`No valid complete path found for ${field}=${value}. Keeping minimal state.`);
            Object.keys(currentSelections).forEach(key => delete currentSelections[key]);
            currentSelections[primaryField] = previousSelections[primaryField];
            currentSelections[field] = value;
        }

        // Update Modal State and UI
        this.previewProductSelection(modal, card, currentSelections);
        this.refreshAllOptionDisplays(modal, card, currentSelections);
        this.ensureHiddenNAFieldsSelected(modal, currentSelections);

        // Handle propagation if needed
        if (propagationNeeded && pathFound) {
            // console.log('[Propagation] Changes require updating related cards:', propagationChanges);
            // Store propagation info for handleSave to process
            modal._pendingPropagation = propagationChanges;
        }
    }

    /**
     * Find the nearest valid alternative when user's selection isn't possible
     */
    findNearestValidAlternative(cardData, targetField, targetValue, previousSelections, allFields) {
        const alternatives = [];
        const targetFieldIndex = allFields.indexOf(targetField);

        // Get all possible values for the target field
        const { allValues } = this.dataService.getPossibleValuesForField(cardData, targetField, {});

        // Score each alternative based on how many previous selections it preserves
        for (const alternativeValue of allValues) {
            if (alternativeValue === targetValue) continue; // Skip the invalid option

            let testSelections = {};
            let preservedCount = 0;
            let changes = {};

            // Try to preserve as many previous selections as possible
            for (let i = 0; i < targetFieldIndex; i++) {
                const field = allFields[i];
                if (previousSelections[field]) {
                    testSelections[field] = previousSelections[field];
                }
            }

            // Set the alternative value
            testSelections[targetField] = alternativeValue;

            // Try to complete the path
            if (this.dataService.findCompleteValidPath(cardData, targetField, alternativeValue, testSelections, allFields, previousSelections)) {
                // Count how many original selections were preserved
                for (const [field, value] of Object.entries(testSelections)) {
                    if (previousSelections[field] === value) {
                        preservedCount++;
                    } else {
                        changes[field] = value;
                    }
                }

                alternatives.push({
                    selections: testSelections,
                    score: preservedCount,
                    changes: changes
                });
            }
        }

        // Return the alternative that preserves the most selections
        if (alternatives.length > 0) {
            alternatives.sort((a, b) => b.score - a.score);
            return alternatives[0];
        }

        return null;
    }

    /**
     * Find the first viable option configuration
     */
    findFirstViableOption(cardData, allFields) {
        const testSelections = {};

        // Recursive function to try all combinations
        const tryField = (fieldIndex) => {
            if (fieldIndex >= allFields.length) {
                // Check if we've reached a valid product
                const skus = this.dataService.deriveProductSKUs(cardData, testSelections);
                return skus && skus.length > 0;
            }

            const fieldName = allFields[fieldIndex];
            const { validValues } = this.dataService.getPossibleValuesForField(cardData, fieldName, testSelections);

            for (const value of validValues) {
                testSelections[fieldName] = value;
                if (tryField(fieldIndex + 1)) {
                    return true;
                }
                delete testSelections[fieldName];
            }

            return false;
        };

        if (tryField(0)) {
            return { ...testSelections };
        }

        return null;
    }

    /**
     * Check if a field is a propagating field
     */
    isPropagatingField(field) {
        return (this.builder.constructor.PROPAGATING_FIELDS || ['Case Type', 'Connection Type']).includes(field);
    }

    /**
     * Get list of propagating fields
     */
    getPropagatingFields() {
        return this.builder.constructor.PROPAGATING_FIELDS || ['Case Type', 'Connection Type'];
    }

    async handleSave(modal, card, currentSelections) {
        // console.log('[handleModalSave] Confirm clicked. Final temp selections:', JSON.stringify(currentSelections));

        const cardTitleForLog = card.getAttribute('data-card-title') || 'Unknown Card';
        const selectionsToSave = { ...currentSelections };
        const cardDataForDerivation = this.dataService.getCurrentCardData(card);

        // Get previous selections for change detection
        const previousSelectionsAttr = card.getAttribute('data-selections');
        let previousSelections = {};
        if (previousSelectionsAttr) {
            try {
                previousSelections = JSON.parse(previousSelectionsAttr);
                // Remove internal flags before comparison
                delete previousSelections._isSecondaryProduct;
                delete previousSelections._directSku;
                delete previousSelections._noPropagate;
                delete previousSelections._isolatedCard;
                delete previousSelections._cardIndex;
            } catch (e) {
                console.error('[handleModalSave] Error parsing previous selections:', e);
            }
        }

        // Check if selections actually changed
        const selectionsChanged = JSON.stringify(previousSelections) !== JSON.stringify(selectionsToSave);

        // Save confirmed selections to the card attribute
        card.setAttribute('data-selections', JSON.stringify(selectionsToSave));

        // Get pending SKUs from modal preview
        let productSKUs = null;
        let isModalDrivenSkuChange = false;

        if (modal._pendingSKUs && modal._pendingSKUs.length > 0) {
            productSKUs = modal._pendingSKUs;
            isModalDrivenSkuChange = true;
            delete modal._pendingSKUs;
        } else if (cardDataForDerivation) {
            productSKUs = this.dataService.deriveProductSKUs(cardDataForDerivation, selectionsToSave);
        }

        // Debug log to see what SKUs we got
        // console.log(`[handleModalSave] Derived SKUs for ${cardTitleForLog}:`, productSKUs);

        // Close the modal with confirmed flag
        this.close(modal, true);

        const pendingPropagation = modal._pendingPropagation;
        delete modal._pendingPropagation;

        // Update cards and propagate after modal closes
        setTimeout(async () => {
            try {
                const container = card.closest('#main-cards-container, #additional-cards-container');
                const cardTitle = card.getAttribute('data-card-title');

                // Update the source card first
                if (productSKUs && productSKUs.length > 0) {
                    await this.dataService.fetchAndUpdateCardData(card, productSKUs[0], selectionsToSave, isModalDrivenSkuChange);

                    if (productSKUs.length > 1) {
                        // console.log(`[handleModalSave] Creating ${productSKUs.length - 1} secondary cards for ${cardTitle}`);

                        // Make sure we're calling the right method from cardManager.ui
                        if (this.cardManager?.ui?.handleAdditionalProducts) {
                            await this.cardManager.ui.handleAdditionalProducts(card, productSKUs.slice(1), selectionsToSave);
                        } else {
                            console.error('[handleModalSave] handleAdditionalProducts method not found on cardManager.ui');
                            // Log what's available
                            // console.log('[handleModalSave] Available on cardManager:', Object.keys(this.cardManager || {}));
                            // console.log('[handleModalSave] Available on cardManager.ui:', Object.keys(this.cardManager?.ui || {}));
                        }
                    } else {
                        // console.log(`[handleModalSave] No secondary cards needed for ${cardTitle}`);
                        // Call removeSecondaryCards from the UI service
                        if (this.cardManager?.ui?.removeSecondaryCards) {
                            this.cardManager.ui.removeSecondaryCards(card);
                        } else {
                            console.error('[handleModalSave] removeSecondaryCards not found on cardManager.ui');
                        }
                    }
                } else {
                    console.warn(`[handleModalSave] No valid product SKUs found for the selections on card ${cardTitle}`);
                    this.cardManager.ui.updateCardAvailabilityStyling(card, false);
                    card.removeAttribute('data-product-price');
                    const titleElements = card.querySelectorAll('.product-title');
                    titleElements.forEach(el => el.textContent = 'Configuration invalid');

                    // Call removeSecondaryCards from the UI service
                    if (this.cardManager?.ui?.removeSecondaryCards) {
                        this.cardManager.ui.removeSecondaryCards(card);
                    } else {
                        console.error('[handleModalSave] removeSecondaryCards not found on cardManager.ui');
                    }
                }

                // Handle propagation for propagating fields
                const changedPropagatingFields = [];
                for (const field of this.builder.constructor.PROPAGATING_FIELDS || ['Case Type', 'Connection Type']) {
                    if (selectionsToSave.hasOwnProperty(field) && previousSelections[field] !== selectionsToSave[field]) {
                        await this.cardManager.handleRelatedCardsUpdate(field, selectionsToSave[field], card);
                        changedPropagatingFields.push({
                            field: field,
                            value: selectionsToSave[field]
                        });
                    } else if (!selectionsToSave.hasOwnProperty(field) && previousSelections.hasOwnProperty(field)) {
                        await this.cardManager.handleRelatedCardsUpdate(field, undefined, card);
                    }
                }

                // Final UI updates
                this.cardManager.ui.updateSubtotal();
                this.cardManager.ui.setupFieldVisibility();
                if (this.builder.eventHandler?.cardInteractionEventHandler) {
                    this.builder.eventHandler.cardInteractionEventHandler.setupListeners();
                }

                // Always use snapshots for step 3 changes
                if (this.builder.stateManager.currentStep === 3 && selectionsChanged) {
                    await this.builder.urlManager.updateURLWithNewSnapshotId(true, true);
                } else if (this.builder.stateManager.currentStep < 3) {
                    this.builder.stateManager.updateURLWithContext(true);
                }

                // Handle automatic propagation from intelligent fallback
                if (pendingPropagation && Object.keys(pendingPropagation).length > 0) {
                    // console.log('[handleSave] Processing automatic propagation from fallback:', pendingPropagation);
                    for (const [field, value] of Object.entries(pendingPropagation)) {
                        if (this.isPropagatingField(field)) {
                            await this.cardManager.handleRelatedCardsUpdate(field, value, card);
                        }
                    }
                }
            } catch (error) {
                console.error("[handleModalSave] Error during async operations:", error);
                console.error("[handleModalSave] Error stack:", error.stack);
                this.builder.uiFeedback.showError("An error occurred while updating products.");
            }
        }, 310);
    }

    // Add all the helper methods that are referenced
    determineDefaultSelections(cardData) {
        let defaultSelections = {};

        if (!cardData || !cardData.default) return defaultSelections;

        if (Array.isArray(cardData.default)) {
            const defaultSKU = cardData.default[0];
            if (cardData.dropdowns) {
                const fields = cardData.dropdowns.map(dd => Object.keys(dd)[0]);
                const derivedSelections = this.dataService._deriveSelectionsFromSKU(cardData.options, defaultSKU, fields);
                if (derivedSelections) {
                    defaultSelections = derivedSelections;
                }
            }
        } else if (typeof cardData.default === 'object') {
            const firstDefaultKey = Object.keys(cardData.default)[0];
            if (Array.isArray(cardData.default[firstDefaultKey]) && cardData.default[firstDefaultKey].length > 0) {
                const defaultSKU = cardData.default[firstDefaultKey][0];
                if (cardData.dropdowns) {
                    const fields = cardData.dropdowns.map(dd => Object.keys(dd)[0]);
                    const derivedSelections = this.dataService._deriveSelectionsFromSKU(cardData.options, defaultSKU, fields);
                    if (derivedSelections) {
                        defaultSelections = derivedSelections;
                    }
                }
            }
        }

        return defaultSelections;
    }

    previewProductSelection(modal, card, currentSelections) {
        const cardData = this.dataService.getCurrentCardData(card);
        const productSKUs = this.dataService.deriveProductSKUs(cardData, currentSelections);
        modal._pendingSKUs = (productSKUs && productSKUs.length > 0) ? productSKUs : null;
        if (modal._pendingSKUs) {
            this.builder.apiService.preloadProductImage(modal._pendingSKUs[0]);
        }
    }

    refreshAllOptionDisplays(modal, card, currentSelections) {
        // console.log('[4] UI Refresh: Refreshing displays with final selections:', JSON.parse(JSON.stringify(currentSelections)));
        const cardData = this.dataService.getCurrentCardData(card);
        if (!cardData) return;

        const desktopContainer = modal.querySelector('.desktop-modal-content .modal-options-container');
        const mobileContainer = modal.querySelector('.mobile-modal-content .modal-options-container');

        this._updateOptionStatesInContainer(desktopContainer, cardData, currentSelections);
        this._updateOptionStatesInContainer(mobileContainer, cardData, currentSelections);
    }

    _updateOptionStatesInContainer(container, cardData, currentSelections) {
        if (!container) return;

        cardData.dropdowns.forEach((dropdownObj) => {
            const fieldName = Object.keys(dropdownObj)[0];
            const fieldGroup = container.querySelector(`.option-group[data-field-group="${fieldName}"]`);
            if (!fieldGroup) return;

            const { validValues } = this.dataService.getPossibleValuesForField(cardData, fieldName, currentSelections);
            const validValuesSet = new Set(validValues);
            const selectedValue = currentSelections[fieldName];

            const optionElements = fieldGroup.querySelectorAll('[data-value]');

            optionElements.forEach(element => {
                const value = element.getAttribute('data-value');
                const isSelected = (value === selectedValue);
                const isValid = validValuesSet.has(value);

                this.updateOptionElementStyle(element, value, fieldName, isSelected, isValid);
            });

            // Update dropdowns - DON'T disable options, just style them
            const select = fieldGroup.querySelector('select[data-field]');
            if (select) {
                select.value = selectedValue || '';
                const options = select.querySelectorAll('option');
                options.forEach(option => {
                    const optionValue = option.value;
                    const isValid = validValuesSet.has(optionValue);
                    option.disabled = false;
                    option.className = isValid ? 'text-gray-700' : 'text-gray-400';
                });
            }
        });
    }

    updateOptionElementStyle(element, value, fieldName, isSelected, isValid) {
        const displayInfo = this.getOptionDisplayType(fieldName, value, isSelected);

        // Generic styling - but DON'T apply border-transparent to buttons
        element.classList.toggle('border-[#00B588]', isSelected);
        element.classList.toggle('ring-2', isSelected);
        element.classList.toggle('ring-[#00B588]', isSelected);

        // Only toggle border-transparent for non-button elements
        if (displayInfo.displayType !== 'Button') {
            element.classList.toggle('border-transparent', !isSelected);
        }

        // CHANGED: Only apply opacity to indicate visual state, but keep clickable
        element.classList.toggle('opacity-50', !isValid && !isSelected);

        // ADDED: Ensure pointer-events are always auto so clicks work
        element.style.pointerEvents = 'auto';
        element.classList.add('cursor-pointer');

        // Type-specific styling
        switch(displayInfo.displayType) {
            case 'Button':
                // Remove all border classes first to avoid conflicts
                element.classList.remove('border-gray', 'border-green', 'border-gray-200', 'border-transparent');

                // ADDED: Always ensure clicks work
                element.classList.add('cursor-pointer');
                element.style.pointerEvents = 'auto';

                // Then add the appropriate border class
                if (isSelected) {
                    element.classList.add('bg-green', 'text-white', 'border-green', 'ring-offset-1');
                    element.classList.remove('bg-white', 'text-gray-700', 'bg-gray-100', 'text-gray-400');
                } else if (isValid) {
                    element.classList.add('bg-white', 'text-gray-700', 'border-gray');
                    element.classList.remove('bg-green', 'text-white', 'border-green', 'ring-offset-1', 'bg-gray-100', 'text-gray-400', 'border-gray-200');
                } else {
                    element.classList.add('bg-gray-100', 'text-gray-400', 'border-gray-200');
                    element.classList.remove('bg-green', 'text-white', 'border-green', 'ring-offset-1', 'bg-white', 'text-gray-700', 'border-gray');
                }

                // Always allow hover effects for better UX
                element.classList.add('hover:shadow-md', 'hover:border-gray-400');
                break;

            case 'Icon':
                element.classList.add('cursor-pointer');
                element.style.pointerEvents = 'auto';
                element.style.cursor = 'pointer';
                element.classList.toggle('border-transparent', !isSelected);
                element.classList.toggle('filter', !isValid && !isSelected);
                element.classList.toggle('grayscale', !isValid && !isSelected);

                // Always allow hover effects
                element.classList.add('hover:bg-gray-100', 'hover:border-gray-300');

                // Remove any pointer-events: none from children
                const children = element.querySelectorAll('*');
                children.forEach(child => {
                    child.style.pointerEvents = 'none'; // Children shouldn't intercept clicks
                });

                const svgContainer = element.querySelector('.icon-container');
                if (svgContainer) {
                    const svgToRender = isSelected
                        ? (displayInfo.svgs?.selected || displayInfo.svgContent)
                        : (displayInfo.svgs?.unselected || displayInfo.svgContent);
                    if (svgToRender) {
                        svgContainer.innerHTML = svgToRender;
                    }
                }

                const textLabel = element.querySelector('span');
                if (textLabel) {
                    textLabel.classList.toggle('text-gray-700', isSelected);
                    textLabel.classList.toggle('text-gray-600', !isSelected && isValid);
                    textLabel.classList.toggle('text-gray-400', !isSelected && !isValid);
                }
                break;

            case 'Circle with Color':
                element.classList.add('cursor-pointer');
                element.style.pointerEvents = 'auto';
                element.classList.toggle('border-transparent', !isSelected);

                // Always allow hover effects
                element.classList.add('hover:bg-gray-100', 'hover:border-gray-300');

                const colorCircle = element.querySelector('.w-6.h-6.rounded-full');
                if (colorCircle) {
                    colorCircle.classList.toggle('ring-2', isSelected);
                    colorCircle.classList.toggle('ring-offset-2', isSelected);
                    colorCircle.classList.toggle('ring-[#00B588]', isSelected);
                    colorCircle.classList.toggle('filter', !isValid && !isSelected);
                    colorCircle.classList.toggle('grayscale', !isValid && !isSelected);

                    if (displayInfo.svgContent && value === 'N/A') {
                        const naColorStroke = isSelected ? '#00b189' : (isValid ? '#a6a6a6' : '#d1d5db');
                        const strokeColor = isValid || isSelected ? 'red' : '#f87171';
                        colorCircle.innerHTML = `<svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="10" fill="white" stroke="${naColorStroke}" stroke-width="1.5"/><line x1="4" y1="20" x2="20" y2="4" stroke="${strokeColor}" stroke-width="2.5"/></svg>`;
                        colorCircle.style.backgroundColor = '';
                    } else if (displayInfo.color) {
                        colorCircle.style.backgroundColor = displayInfo.color;
                        colorCircle.innerHTML = '';
                    }
                }

                const colorTextLabel = element.querySelector('span');
                if (colorTextLabel) {
                    colorTextLabel.classList.toggle('text-gray-700', isSelected);
                    colorTextLabel.classList.toggle('text-gray-600', !isSelected && isValid);
                    colorTextLabel.classList.toggle('text-gray-400', !isSelected && !isValid);
                }
                break;

            case 'Checkbox':
                const checkbox = element.matches('input[type="checkbox"]') ? element : element.querySelector('input[type="checkbox"]');
                if (checkbox) {
                    checkbox.checked = isSelected;
                    checkbox.style.pointerEvents = 'auto';
                    checkbox.disabled = false; // Ensure checkbox is never disabled

                    const label = checkbox.closest('div').querySelector('label');
                    if (label) {
                        label.classList.toggle('text-gray-700', isSelected || isValid);
                        label.classList.toggle('text-gray-400', !isSelected && !isValid);
                        label.style.cursor = 'pointer';
                    }
                    // Only apply opacity to parent container, not disable functionality
                    checkbox.parentElement.classList.toggle('opacity-50', !isValid && !isSelected);
                }
                break;
        }
    }

    ensureHiddenNAFieldsSelected(modal, currentSelections) {
        if (!modal || !currentSelections) return;
        modal.querySelectorAll('input[data-auto-selected="true"]').forEach(input => {
            const field = input.getAttribute('data-field');
            const value = input.value;
            if (field && value && !currentSelections.hasOwnProperty(field)) {
                currentSelections[field] = value;
            }
        });
    }

    setupFieldVisibility() {
        if (this.cardManager?.ui?.setupFieldVisibility) {
            this.cardManager.ui.setupFieldVisibility();
        }
    }

    // Add all the animation and scroll prevention methods
    animateDesktopModal(modal, backdrop) {
        const contentContainer = modal.querySelector('.md\\:flex.items-center.justify-center');
        const content = contentContainer?.querySelector('.desktop-modal-content');

        if (content) {
            content.style.transform = '';
            content.style.transition = 'transform 0.3s ease-out';
            content.classList.add('scale-95');
            content.classList.remove('scale-100');

            void modal.offsetWidth;

            if (backdrop) {
                backdrop.style.transition = 'opacity 0.3s ease-out';
                backdrop.style.opacity = '1';
            }

            setTimeout(() => {
                content.classList.remove('scale-95');
                content.classList.add('scale-100');
            }, 10);
        }
    }

    animateMobileModal(modal, backdrop) {
        const mobileContent = modal.querySelector('.mobile-modal-content');
        if (mobileContent) {
            mobileContent.style.transform = '';
            mobileContent.style.transition = 'transform 0.3s ease-out';
            mobileContent.classList.add('translate-y-full');
            mobileContent.classList.remove('translate-y-0');

            void modal.offsetWidth;

            if (backdrop) {
                backdrop.style.transition = 'opacity 0.3s ease-out';
                backdrop.style.opacity = '1';
            }

            setTimeout(() => {
                mobileContent.classList.remove('translate-y-full');
                mobileContent.classList.add('translate-y-0');
            }, 10);
        }
    }

    animateModalClose(modal, content, backdrop, isMobile) {
        if (backdrop) {
            backdrop.style.opacity = '0';
        }

        if (isMobile) {
            content.classList.remove('translate-y-0');
            content.classList.add('translate-y-full');
        } else {
            content.classList.remove('scale-100');
            content.classList.add('scale-95');
        }

        setTimeout(() => {
            modal.classList.add('hidden');
            document.body.classList.remove('overflow-hidden');
        }, 300);
    }

    setupModalScrollPrevention(modal) {
        modal.addEventListener('wheel', (e) => {
            if (e.target === modal ||
                e.target.classList.contains('preview-modal-backdrop') ||
                e.target.classList.contains('modal-backdrop')) {
                e.preventDefault();
                e.stopPropagation();
            }
        }, { passive: false });

        modal.addEventListener('touchmove', (e) => {
            if (e.target === modal ||
                e.target.classList.contains('preview-modal-backdrop') ||
                e.target.classList.contains('modal-backdrop')) {
                e.preventDefault();
                e.stopPropagation();
            }
        }, { passive: false });

        const scrollableAreas = modal.querySelectorAll(
            '.preview-modal-content, .mobile-preview-content, .overflow-y-auto, .modal-options-container, .mobile-modal-content'
        );
        scrollableAreas.forEach(area => {
            area.style.overscrollBehavior = 'contain';
        });
    }

    setupMobileScrollPrevention(modal) {
        if (!modal) return;

        const mobileContent = modal.querySelector('.mobile-modal-content');
        if (!mobileContent) return;

        modal.addEventListener('touchmove', (e) => {
            if (e.target === modal ||
                e.target.classList.contains('modal-backdrop') ||
                e.target === mobileContent.parentElement) {
                e.preventDefault();
                e.stopPropagation();
            }
        }, { passive: false });

        const optionsContainer = mobileContent.querySelector('.modal-options-container, .space-y-4');
        if (optionsContainer) {
            this.setupOptionsScrollPrevention(optionsContainer);
        }

        mobileContent.addEventListener('touchmove', (e) => {
            const scrollableArea = e.target.closest('.overflow-y-auto, .modal-options-container, .space-y-4, .p-5');

            if (!scrollableArea) {
                e.preventDefault();
                e.stopPropagation();
                return;
            }

            const isInOptionsArea = e.target.closest('.modal-options-container, .space-y-4');
            if (isInOptionsArea) {
                return;
            }

            const isAtTop = scrollableArea.scrollTop === 0;
            const isAtBottom = scrollableArea.scrollTop + scrollableArea.clientHeight >= scrollableArea.scrollHeight;

            const touch = e.touches[0];
            if (!touch) return;

            if (!scrollableArea._startY) {
                scrollableArea._startY = touch.clientY;
            }

            const deltaY = touch.clientY - scrollableArea._startY;

            if ((isAtTop && deltaY > 0) || (isAtBottom && deltaY < 0)) {
                e.preventDefault();
                e.stopPropagation();
            }
        }, { passive: false });

        mobileContent.addEventListener('touchend', () => {
            const scrollableAreas = mobileContent.querySelectorAll('.overflow-y-auto, .modal-options-container, .space-y-4, .p-5');
            scrollableAreas.forEach(area => {
                delete area._startY;
            });
        });

        modal._mobileScrollPreventionSetup = true;
    }

    setupOptionsScrollPrevention(container) {
        if (!container) return;

        container.addEventListener('touchmove', (e) => {
            const scrollableElement = e.target.closest('.overflow-y-auto, .modal-options-container');
            if (scrollableElement) {
                const isAtTop = scrollableElement.scrollTop === 0;
                const isAtBottom = scrollableElement.scrollTop + scrollableElement.clientHeight >= scrollableElement.scrollHeight;

                if ((isAtTop && e.touches[0].clientY > scrollableElement._lastY) ||
                    (isAtBottom && e.touches[0].clientY < scrollableElement._lastY)) {
                    e.preventDefault();
                }

                scrollableElement._lastY = e.touches[0].clientY;
            }
        }, { passive: false });

        container.addEventListener('touchstart', (e) => {
            const scrollableElement = e.target.closest('.overflow-y-auto, .modal-options-container');
            if (scrollableElement) {
                scrollableElement._lastY = e.touches[0].clientY;
            }
        });
    }

    setupMobileModalSwipe(modal, mobileContent) {
        if (!mobileContent) return;

        const dragHandleBar = mobileContent.querySelector('.w-full.py-3.flex.justify-center');
        const customizeHeaderDiv = mobileContent.querySelector('.px-4.py-2.flex.items-center');
        const scrollableContent = mobileContent.querySelector('.overflow-y-auto');

        let startY = 0;
        let currentY = 0;
        let isDragging = false;
        let startTime = 0;

        const onTouchStart = (e) => {
            const isOnDragHandle = e.target.closest('.w-full.py-3.flex.justify-center');
            const isOnHeader = e.target.closest('.px-4.py-2.flex.items-center');
            const isAtTop = scrollableContent ? scrollableContent.scrollTop === 0 : true;

            if (!isOnDragHandle && !isOnHeader && !isAtTop) {
                return;
            }

            e.preventDefault();
            startY = e.touches[0].clientY;
            currentY = startY;
            startTime = Date.now();
            isDragging = true;
            mobileContent.style.transition = 'none';
        };

        const onTouchMove = (e) => {
            if (!isDragging) return;

            currentY = e.touches[0].clientY;
            const deltaY = currentY - startY;

            if (deltaY > 0) {
                e.preventDefault();
                const resistance = Math.min(1, Math.max(0.3, 1 - (deltaY / window.innerHeight)));
                const adjustedDelta = deltaY * resistance;
                mobileContent.style.transform = `translateY(${adjustedDelta}px)`;
                const opacity = Math.max(0.7, 1 - (deltaY / window.innerHeight));
                mobileContent.style.opacity = opacity;
            }
        };

        const onTouchEnd = () => {
            if (!isDragging) return;

            isDragging = false;
            mobileContent.style.transition = 'transform 0.3s ease-out, opacity 0.3s ease-out';

            const deltaY = currentY - startY;
            const deltaTime = Date.now() - startTime;
            const velocity = deltaTime > 0 ? deltaY / deltaTime : 0;

            const shouldClose = deltaY > 100 || (velocity > 0.5 && deltaY > 50);

            if (shouldClose) {
                mobileContent.style.transform = '';
                mobileContent.style.opacity = '';
                this.close(modal);
            } else {
                mobileContent.style.transform = '';
                mobileContent.style.opacity = '';
            }
        };

        if (dragHandleBar) {
            dragHandleBar.addEventListener('touchstart', onTouchStart, { passive: false });
        }
        if (customizeHeaderDiv) {
            customizeHeaderDiv.addEventListener('touchstart', onTouchStart, { passive: false });
        }

        mobileContent.addEventListener('touchmove', (e) => {
            if (isDragging) {
                e.preventDefault();
                onTouchMove(e);
            } else {
                const target = e.target.closest('.overflow-y-auto, .modal-options-container');
                if (target) {
                    const isAtTop = target.scrollTop === 0;
                    const isAtBottom = target.scrollTop + target.clientHeight >= target.scrollHeight;

                    const touch = e.touches[0];
                    if (!touch) return;

                    const startY = touch.clientY;
                    const deltaY = startY - (target._lastTouchY || startY);
                    target._lastTouchY = startY;

                    if ((isAtTop && deltaY > 0) || (isAtBottom && deltaY < 0)) {
                        e.preventDefault();
                    }
                } else {
                    e.preventDefault();
                }
            }
        }, { passive: false });

        mobileContent.addEventListener('touchend', (e) => {
            const scrollableAreas = mobileContent.querySelectorAll('.overflow-y-auto, .modal-options-container');
            scrollableAreas.forEach(area => {
                delete area._lastTouchY;
            });
            onTouchEnd();
        });

        mobileContent.addEventListener('touchcancel', onTouchEnd);
    }

    restorePageScroll(modal = null) {
        if (!document.querySelector('.product-modal:not(.hidden)') &&
            !document.querySelector('.preview-modal[style*="opacity: 1"]')) {

            const scrollPosition = modal && modal._scrollPosition !== undefined ? modal._scrollPosition : 0;

            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            document.body.style.overscrollBehavior = '';

            requestAnimationFrame(() => {
                if (scrollPosition > 0) {
                    window.scrollTo(0, scrollPosition);
                }
            });
        }
    }

    // Add the missing helper methods from the original
    getAllPossibleValuesForField(optionsTree, targetFieldName, fieldDefinitions) {
        const fieldNames = fieldDefinitions.map(dropdown => Object.keys(dropdown)[0]);
        const targetFieldIndex = fieldNames.indexOf(targetFieldName);

        if (targetFieldIndex === -1) {
            console.warn(`Field ${targetFieldName} not found in dropdown definitions`);
            return [];
        }

        const valuesSet = new Set();

        function traverseTree(node, depth) {
            if (depth === targetFieldIndex) {
                if (node && typeof node === 'object' && !Array.isArray(node)) {
                    Object.keys(node).forEach(key => valuesSet.add(key));
                }
                return;
            }

            if (!node || typeof node !== 'object' || Array.isArray(node)) {
                return;
            }

            Object.keys(node).forEach(key => {
                traverseTree(node[key], depth + 1);
            });
        }

        traverseTree(optionsTree, 0);
        return Array.from(valuesSet);
    }

    getPossibleValuesForField(cardData, field, currentSelections) {
        return this.dataService.getPossibleValuesForField(cardData, field, currentSelections);
    }

    renderOptionSection(fieldLabel, fieldName, allValues, validValues, selectedValue, displayInfo) {
      // Return empty string if there are no possible values for this option group
      if (!allValues || allValues.length === 0) {
        return '';
      }

      // SPECIAL CASE: If there's only one option and it's "N/A", we'll hide this section visually
      // but still include it in the DOM with the value pre-selected
      const isSingleNA = allValues.length === 1 && allValues[0] === 'N/A';

      // SPECIAL CASE 2: If there's only one option and it's "1" (assuming '1' is a string)
      // If '1' could be a number, use: const isSingleOne = allValues.length === 1 && allValues[0] === 1;
      const isSingleOne = allValues.length === 1

      // If single N/A and not already selected, auto-select it
      if (isSingleNA && selectedValue !== 'N/A') {
        selectedValue = 'N/A';
      }

      // Determine if the section should be visually hidden
      const hideSection = isSingleNA || isSingleOne;

      // Start building the HTML for the option group
      let html = `<div class="mb-4 option-group ${hideSection ? 'hidden' : ''}" data-field-group="${fieldName}"`; // Group container

      // Add data attribute to mark as single N/A for potential future reference
      if (isSingleNA) {
        html += ` data-single-na="true"`;
      }

      html += `>`;
      html += `<label class="block text-sm font-medium text-gray-700 mb-2">${fieldLabel}</label>`; // Label for the group
      html += `<div class="option-container flex flex-wrap gap-2" data-field="${fieldName}">`; // Container for the actual options

      // Use a Set for efficient checking of whether a value is currently valid
      const validValuesSet = new Set(validValues);

      try {
        // Determine the general display type for the section based on the first value.
        const sectionDisplayType = displayInfo.displayType;

        // Render the options based on the determined display type
        switch (sectionDisplayType) {
          case 'Button':
          case 'Icon':
          case 'Circle with Color':
            // For types rendered individually, loop through all possible values
            allValues.forEach(value => {
              const isSelected = value === selectedValue; // Is this specific value the selected one?
              const isValid = validValuesSet.has(value); // Is this specific value currently valid?

              // Properly escape value for data-value attribute
              const escapedValue = value.replace(/"/g, '&quot;');

              const specificDisplayInfo = this.getOptionDisplayType(fieldName, value, isSelected);

              // Render option based on type with escaped attribute
              let optionHtml = '';

              switch (specificDisplayInfo.displayType) {
                case 'Button':
                  optionHtml = `
                    <button type="button"
                      class="px-3 py-1.5 rounded-md text-sm transition-all duration-150 border
                            ${isSelected ? 'bg-green text-white border-green ring-2 ring-offset-1 ring-green' : 'bg-white text-gray-700 border-gray'}
                            ${!isValid && !isSelected ? 'bg-gray-100 text-gray-400 border-gray-200' : ''}
                            ${isValid ? 'hover:shadow-md hover:border-gray-400' : ''}
                            ${!isValid ? 'opacity-50' : ''}"
                      data-value="${escapedValue}">
                      ${value}
                    </button>
                  `;
                  break;

                case 'Icon':
                  optionHtml = `
                    <div class="icon-option text-center cursor-pointer p-1 border rounded-md transition-all duration-150
                                ${isSelected ? 'border-[#00B588] ring-2 ring-[#00B588]' : 'border-transparent'}
                                ${!isValid && !isSelected ? 'opacity-50 filter grayscale' : ''}
                                hover:bg-gray-100 hover:border-gray-300"
                        data-value="${escapedValue}">
                      <div class="icon-container h-10 w-10 flex items-center justify-center mx-auto mb-1">
                        ${specificDisplayInfo.svgContent || ''}
                      </div>
                      <span class="block text-xs text-center text-gray-600">${value}</span>
                    </div>
                  `;
                  break;

                case 'Circle with Color':
                  optionHtml = `
                    <div class="flex flex-col items-center text-center cursor-pointer p-1 border rounded-md transition-all duration-150
                                ${isSelected ? 'border-[#00B588] ring-2 ring-[#00B588]' : 'border-transparent'}
                                ${!isValid ? 'opacity-50' : 'hover:bg-gray-100 hover:border-gray-300'}"
                        data-value="${escapedValue}">
                      <div class="w-6 h-6 rounded-full border border-gray-300 mb-1 ${isSelected ? 'ring-2 ring-offset-2 ring-[#00B588]' : ''}"
                          style="background-color: ${specificDisplayInfo.color || '#ffffff'};">
                      </div>
                      <span class="block text-xs text-center text-gray-600">${value}</span>
                    </div>
                  `;
                  break;
              }

              html += optionHtml;
            });
            break;

          case 'Checkbox':
            // Identify if this is a Yes/N/A checkbox field
            const hasYes = allValues.includes('Yes');
            const hasNA = allValues.includes('N/A');
            const isYesNACase = hasYes && hasNA && allValues.length === 2;

            // Set the proper value to use for the checkbox
            const checkboxValue = hasYes ? 'Yes' : allValues[0] || 'Yes';

            // Determine checkbox state
            const isChecked = isYesNACase
              ? selectedValue === 'Yes'  // In Yes/NA case, only check if "Yes" is selected
              : checkboxValue === selectedValue;

            // Determine if the option is valid
            const isValid = isYesNACase
              ? validValuesSet.has('Yes') || validValuesSet.has('N/A')
              : validValuesSet.has(checkboxValue);

            // Generate a unique ID for the checkbox element
            const uniqueId = `option-${fieldName.replace(/[^a-zA-Z0-9]/g, '-')}-${Math.random().toString(36).substring(7)}`;

            // For Yes/N/A case, store N/A as the alternative value
            const alternateValue = isYesNACase ? 'N/A' : '';

            html += `
              <div class="flex items-center space-x-2 ${!isValid ? 'opacity-50' : ''}">
                <input type="checkbox"
                    id="${uniqueId}"
                    class="form-checkbox h-4 w-4 text-[#00B588] rounded focus:ring-[#00B588] border-gray-300 cursor-pointer"
                    style="accent-color: #00B588;"
                    ${isChecked ? 'checked' : ''}
                    data-value="${checkboxValue}"
                    ${isYesNACase ? `data-alternate-value="${alternateValue}"` : ''}
                    data-is-yes-na="${isYesNACase}">
                <label for="${uniqueId}" class="text-sm text-gray-700 cursor-pointer">
                    ${fieldLabel}</label>
              </div>
            `;
            break;

          case 'Drop Down':
          default:
            // Dropdowns need to list all options, indicating which are invalid
            html += `<select class="w-full border border-gray rounded-md py-2 px-3 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent" data-field="${fieldName}">`;
            // html += `<option value="">Select ${fieldLabel}</option>`; // Add a default "Select..." prompt
            allValues.forEach(value => {
              const isSelected = value === selectedValue; // Is this option the selected one?
              const isValid = validValuesSet.has(value); // Is this option currently valid?

              // Properly escape quotes in the value attribute
              const escapedValue = value.replace(/"/g, '&quot;');

              // Add the option with properly escaped value
              html += `<option value="${escapedValue}" ${isSelected ? 'selected' : ''} ${!isValid ? 'class="text-gray-400"' : 'class="text-gray-700"'}>${value}</option>`;
            });
            html += `</select>`;
            break;
        }
      } catch (error) {
        console.error(`Error rendering options for ${fieldName}:`, error);
        html += `<p class="text-sm text-red-500">Error loading options: ${error.message}</p>`;
      }

      // Close the containers
      html += `</div>`; // Close option-container
      html += `</div>`; // Close option-group

      // For single N/A, add a hidden input to ensure the value is included in form submission
      if (isSingleNA) {
        html += `<input type="hidden" name="${fieldName}" value="N/A" data-field="${fieldName}" data-auto-selected="true">`;
      }

      return html;
    }

    getOptionDisplayType(field, value, isSelected = false) {
        const defaultDisplay = { displayType: 'Drop Down' };
        if (value === undefined) return defaultDisplay;

        const buttonFields = [
            'Long Arm', 'Medium Arm', 'Short Arm',
            'Hard Case', 'Next Gen', 'Thin-Case™',
            'Drill-Down', 'Magnetic',
            'Standard Button', 'Hand Strap Button',
            'Hand-Stand™ Button', 'Release Lever',
            'Spring Loaded', 'Release Button'
        ];
        if (buttonFields.includes(value)) {
            return { displayType: 'Button' };
        }

        if (value === 'Yes') {
            return { displayType: 'Checkbox' };
        }

        const colorMap = {
            'Black': '#000000',
            'Gray': '#808080',
            'White': '#ffffff'
        };

        if (field === 'Color') {
            if (value === 'N/A') {
                const naColorStroke = isSelected ? '#00b189' : '#a6a6a6';
                return {
                    displayType: 'Circle with Color',
                    svgContent: `<svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="10" fill="white" stroke="${naColorStroke}" stroke-width="1.5"/><line x1="4" y1="20" x2="20" y2="4" stroke="red" stroke-width="2.5"/></svg>`
                };
            } else if (colorMap[value]) {
                return {
                    displayType: 'Circle with Color',
                    color: colorMap[value]
                };
            }
        } else if (colorMap[value]) { // If field is not 'Color' but value is a color name
            return {
                displayType: 'Circle with Color',
                color: colorMap[value]
            };
        }

        if (field === 'Connection Type') {
            let greenSvgForValue = '';
            let graySvgForValue = '';

            if (value === 'Bottom D-Shape Pads') {
                greenSvgForValue = `<svg viewBox="0 0 2247.71 364.73" xmlns="http://www.w3.org/2000/svg"><defs><style>.cls-1{fill:#00b189;}</style></defs><g id="D_Shape_Logo"><g id="Pogo_Pins"><path class="cls-1" d="M835.01,133.11c0-21.62-17.59-39.22-39.22-39.22s-39.22,17.59-39.22,39.22,17.59,39.22,39.22,39.22,39.22-17.59,39.22-39.22Z"/><path class="cls-1" d="M734.3,192.26c-21.62,0-39.22,17.59-39.22,39.22s17.59,39.22,39.22,39.22,39.22-17.59,39.22-39.22-17.59-39.22-39.22-39.22Z"/><path class="cls-1" d="M853.17,192.26c-21.62,0-39.22,17.59-39.22,39.22s17.59,39.22,39.22,39.22,39.22-17.59,39.22-39.22-17.59-39.22-39.22-39.22Z"/><path class="cls-1" d="M972.04,192.26c-21.62,0-39.22,17.59-39.22,39.22s17.59,39.22,39.22,39.22,39.22-17.59,39.22-39.22-17.59-39.22-39.22-39.22Z"/><path class="cls-1" d="M1090.91,192.26c-21.62,0-39.22,17.59-39.22,39.22s17.59,39.22,39.22,39.22,39.22-17.59,39.22-39.22-17.59-39.22-39.22-39.22Z"/><path class="cls-1" d="M1209.78,192.26c-21.62,0-39.22,17.59-39.22,39.22s17.59,39.22,39.22,39.22,39.22-17.59,39.22-39.22-17.59-39.22-39.22-39.22Z"/><path class="cls-1" d="M1328.65,192.26c-21.62,0-39.22,17.59-39.22,39.22s17.59,39.22,39.22,39.22,39.22-17.59,39.22-39.22-17.59-39.22-39.22-39.22Z"/><path class="cls-1" d="M954.3,133.11c0-21.62-17.59-39.22-39.22-39.22s-39.22,17.59-39.22,39.22,17.59,39.22,39.22,39.22,39.22-17.59,39.22-39.22Z"/><path class="cls-1" d="M1073.59,133.11c0-21.62-17.59-39.22-39.22-39.22s-39.22,17.59-39.22,39.22,17.59,39.22,39.22,39.22,39.22-17.59,39.22-39.22Z"/><path class="cls-1" d="M1192.88,133.11c0-21.62-17.59-39.22-39.22-39.22s-39.22,17.59-39.22,39.22,17.59,39.22,39.22,39.22,39.22-17.59,39.22-39.22Z"/><path class="cls-1" d="M1312.16,133.11c0-21.62-17.59-39.22-39.22-39.22s-39.22,17.59-39.22,39.22,17.59,39.22,39.22,39.22,39.22-17.59,39.22-39.22Z"/><path class="cls-1" d="M1392.23,93.89c-21.62,0-39.22,17.59-39.22,39.22s17.59,39.22,39.22,39.22,39.22-17.59,39.22-39.22-17.59-39.22-39.22-39.22Z"/></g><path id="D_Shape" class="cls-1" d="M1943.43,66.56c7.63.36,40,5.06,68.15,22.33,33.02,20.25,48.39,49.93,48.39,93.4s-15.46,75.69-48.66,95.3c-31.44,18.56-66.14,20.37-67.6,20.43H75.64c-5.01,0-9.08-4.07-9.08-9.08V75.64c0-5.01,4.07-9.08,9.08-9.08h1867.79M1943.93,0H75.64C33.86,0,0,33.86,0,75.64v213.3c0,41.77,33.86,75.64,75.64,75.64h1868.29c20.89,0,182.61-15.13,182.61-182.29S1964.81,0,1943.93,0h0Z"/><g id="Trademark"><path class="cls-1" d="M2147.36,320.27h-20.82v-8.07h51.21v8.07h-21.19v44.46h-9.19v-44.46ZM2187.5,312.21h8.63l21.57,27.57,21.76-27.57h8.25v52.52h-9.19v-37.7l-20.26,24.57h-1.69l-19.88-24.95v38.08h-9.19v-52.52Z"/></g></g></svg>`;
                graySvgForValue = `<svg viewBox="0 0 2247.7 364.7" xmlns="http://www.w3.org/2000/svg"><defs><style>.st0{fill:#a6a6a6;}</style></defs><g id="D_Shape_Logo"><g id="Pogo_Pins"><path class="st0" d="M835,133.1c0-21.6-17.6-39.2-39.2-39.2s-39.2,17.6-39.2,39.2,17.6,39.2,39.2,39.2,39.2-17.6,39.2-39.2Z"/><path class="st0" d="M734.3,192.3c-21.6,0-39.2,17.6-39.2,39.2s17.6,39.2,39.2,39.2,39.2-17.6,39.2-39.2-17.6-39.2-39.2-39.2h0Z"/><path class="st0" d="M853.2,192.3c-21.6,0-39.2,17.6-39.2,39.2s17.6,39.2,39.2,39.2,39.2-17.6,39.2-39.2-17.6-39.2-39.2-39.2h0Z"/><path class="st0" d="M972,192.3c-21.6,0-39.2,17.6-39.2,39.2s17.6,39.2,39.2,39.2,39.2-17.6,39.2-39.2-17.6-39.2-39.2-39.2h0Z"/><path class="st0" d="M1090.9,192.3c-21.6,0-39.2,17.6-39.2,39.2s17.6,39.2,39.2,39.2,39.2-17.6,39.2-39.2-17.6-39.2-39.2-39.2h0Z"/><path class="st0" d="M1209.8,192.3c-21.6,0-39.2,17.6-39.2,39.2s17.6,39.2,39.2,39.2,39.2-17.6,39.2-39.2-17.6-39.2-39.2-39.2h0Z"/><path class="st0" d="M1328.7,192.3c-21.6,0-39.2,17.6-39.2,39.2s17.6,39.2,39.2,39.2,39.2-17.6,39.2-39.2-17.6-39.2-39.2-39.2h0Z"/><path class="st0" d="M954.3,133.1c0-21.6-17.6-39.2-39.2-39.2s-39.2,17.6-39.2,39.2,17.6,39.2,39.2,39.2,39.2-17.6,39.2-39.2h0Z"/><path class="st0" d="M1073.6,133.1c0-21.6-17.6-39.2-39.2-39.2s-39.2,17.6-39.2,39.2,17.6,39.2,39.2,39.2,39.2-17.6,39.2-39.2Z"/><path class="st0" d="M1192.9,133.1c0-21.6-17.6-39.2-39.2-39.2s-39.2,17.6-39.2,39.2,17.6,39.2,39.2,39.2,39.2-17.6,39.2-39.2Z"/><path class="st0" d="M1312.2,133.1c0-21.6-17.6-39.2-39.2-39.2s-39.2,17.6-39.2,39.2,17.6,39.2,39.2,39.2,39.2-17.6,39.2-39.2Z"/><path class="st0" d="M1392.2,93.9c-21.6,0-39.2,17.6-39.2,39.2s17.6,39.2,39.2,39.2,39.2-17.6,39.2-39.2-17.6-39.2-39.2-39.2Z"/></g><path id="D_Shape" class="st0" d="M1943.4,66.6c7.6.4,40,5.1,68.2,22.3,33,20.2,48.4,49.9,48.4,93.4s-15.5,75.7-48.7,95.3c-31.4,18.6-66.1,20.4-67.6,20.4H75.6c-5,0-9.1-4.1-9.1-9.1V75.6c0-5,4.1-9.1,9.1-9.1h1867.8M1943.9,0H75.6C33.9,0,0,33.9,0,75.6v213.3c0,41.8,33.9,75.6,75.6,75.6h1868.3c20.9,0,182.6-15.1,182.6-182.3S1964.8,0,1943.9,0h0Z"/><g id="Trademark"><path class="st0" d="M2147.4,320.3h-20.8v-8.1h51.2v8.1h-21.2v44.5h-9.2v-44.5h0ZM2187.5,312.2h8.6l21.6,27.6,21.8-27.6h8.2v52.5h-9.2v-37.7l-20.3,24.6h-1.7l-19.9-25v38.1h-9.2v-52.5h0Z"/></g></g></svg>`;
            } else if (value === 'Rear Pogo Pads') {
                greenSvgForValue = `<svg viewBox="0 0 822.1 823.9" xmlns="http://www.w3.org/2000/svg"><defs><style>.cls-1{fill:#00b188;}</style></defs><g id="Layer_1-2"><path class="cls-1" d="M572.24,822.1H249.87c-5.85,0-11.36-2.28-15.5-6.42L6.42,587.73c-4.14-4.14-6.42-9.64-6.42-15.5V249.87c0-5.85,2.28-11.36,6.42-15.5L234.37,6.42c4.14-4.14,9.64-6.42,15.5-6.42h322.37c5.85,0,11.36,2.28,15.5,6.42l227.95,227.95c4.14,4.14,6.42,9.64,6.42,15.5v322.37c0,5.85-2.28,11.36-6.42,15.5l-227.95,227.95c-4.14,4.14-9.64,6.42-15.5,6.42ZM257.36,782.1h307.39l217.36-217.36v-307.39l-217.36-217.36h-307.39L40,257.36v307.39l217.36,217.36Z"/><g><circle class="cls-1" cx="410.24" cy="130.55" r="15"/><circle class="cls-1" cx="410.24" cy="170.55" r="15"/><circle class="cls-1" cx="410.24" cy="210.55" r="15"/><circle class="cls-1" cx="410.24" cy="250.55" r="15"/><circle class="cls-1" cx="410.24" cy="290.55" r="15"/><circle class="cls-1" cx="410.24" cy="330.55" r="15"/><circle class="cls-1" cx="410.24" cy="370.55" r="15"/></g><circle class="cls-1" cx="410.68" cy="410.55" r="15"/><g><circle class="cls-1" cx="451.05" cy="410.05" r="15"/><circle class="cls-1" cx="491.05" cy="410.05" r="15"/><circle class="cls-1" cx="531.05" cy="410.05" r="15"/><circle class="cls-1" cx="571.05" cy="410.05" r="15"/><circle class="cls-1" cx="611.05" cy="410.05" r="15"/><circle class="cls-1" cx="651.05" cy="410.05" r="15"/><circle class="cls-1" cx="691.05" cy="410.05" r="15"/></g><g><circle class="cls-1" cx="410.24" cy="450.55" r="15"/><circle class="cls-1" cx="410.24" cy="490.55" r="15"/><circle class="cls-1" cx="410.24" cy="530.55" r="15"/><circle class="cls-1" cx="410.24" cy="570.55" r="15"/><circle class="cls-1" cx="410.24" cy="610.55" r="15"/><circle class="cls-1" cx="410.24" cy="650.55" r="15"/><circle class="cls-1" cx="410.24" cy="690.55" r="15"/></g><g><circle class="cls-1" cx="131.3" cy="410.05" r="15"/><circle class="cls-1" cx="171.3" cy="410.05" r="15"/><circle class="cls-1" cx="211.3" cy="410.05" r="15"/><circle class="cls-1" cx="251.3" cy="410.05" r="15"/><circle class="cls-1" cx="291.3" cy="410.05" r="15"/><circle class="cls-1" cx="331.3" cy="410.05" r="15"/><circle class="cls-1" cx="371.3" cy="410.05" r="15"/></g><g id="Trademark"><path class="cls-1" d="M717.01,800.51h-10.95v-4.24h26.94v4.24h-11.15v23.39h-4.84v-23.39ZM738.12,796.27h4.54l11.35,14.51,11.45-14.51h4.34v27.63h-4.84v-19.84l-10.66,12.93h-.89l-10.46-13.13v20.03h-4.84v-27.63Z"/></g></g></svg>`;
                graySvgForValue = `<svg viewBox="0 0 822.1 823.9" xmlns="http://www.w3.org/2000/svg"><defs><style>.st0{fill:#a6a6a6;}</style></defs><g id="Layer_1-2"><g><path class="st0" d="M572.2,822.1H249.9c-5.9,0-11.4-2.3-15.5-6.4L6.4,587.7c-4.1-4.1-6.4-9.6-6.4-15.5V249.9c0-5.9,2.3-11.4,6.4-15.5L234.4,6.4c4.1-4.1,9.6-6.4,15.5-6.4h322.4c5.8,0,11.4,2.3,15.5,6.4l228,227.9c4.1,4.1,6.4,9.6,6.4,15.5v322.4c0,5.8-2.3,11.4-6.4,15.5l-228,228c-4.1,4.1-9.6,6.4-15.5,6.4h0ZM257.4,782.1h307.4l217.4-217.4v-307.4l-217.4-217.4h-307.4L40,257.4v307.4l217.4,217.4h0Z"/><g><circle class="st0" cx="410.2" cy="130.6" r="15"/><circle class="st0" cx="410.2" cy="170.6" r="15"/><circle class="st0" cx="410.2" cy="210.6" r="15"/><circle class="st0" cx="410.2" cy="250.5" r="15"/><circle class="st0" cx="410.2" cy="290.5" r="15"/><circle class="st0" cx="410.2" cy="330.5" r="15"/><circle class="st0" cx="410.2" cy="370.5" r="15"/></g><circle class="st0" cx="410.7" cy="410.5" r="15"/><g><circle class="st0" cx="451" cy="410" r="15"/><circle class="st0" cx="491" cy="410" r="15"/><circle class="st0" cx="531" cy="410" r="15"/><circle class="st0" cx="571" cy="410" r="15"/><circle class="st0" cx="611" cy="410" r="15"/><circle class="st0" cx="651" cy="410" r="15"/><circle class="st0" cx="691" cy="410" r="15"/></g><g><circle class="st0" cx="410.2" cy="450.5" r="15"/><circle class="st0" cx="410.2" cy="490.5" r="15"/><circle class="st0" cx="410.2" cy="530.5" r="15"/><circle class="st0" cx="410.2" cy="570.5" r="15"/><circle class="st0" cx="410.2" cy="610.5" r="15"/><circle class="st0" cx="410.2" cy="650.5" r="15"/><circle class.st0" cx="410.2" cy="690.5" r="15"/></g><g><circle class="st0" cx="131.3" cy="410" r="15"/><circle class="st0" cx="171.3" cy="410" r="15"/><circle class="st0" cx="211.3" cy="410" r="15"/><circle class="st0" cx="251.3" cy="410" r="15" transform="translate(-193.7 592.4) rotate(-80.8)"/><circle class="st0" cx="291.3" cy="410" r="15"/><circle class="st0" cx="331.3" cy="410" r="15"/><circle class="st0" cx="371.3" cy="410" r="15"/></g><g id="Trademark"><path class="st0" d="M717,800.5h-11v-4.2h26.9v4.2h-11.2v23.4h-4.8v-23.4h0ZM738.1,796.3h4.5l11.3,14.5,11.5-14.5h4.3v27.6h-4.8v-19.8l-10.7,12.9h-.9l-10.5-13.1v20h-4.8v-27.6h0Z"/></g></g></g></svg>`;
            } else if (value === 'Bottom D-Shape & Rear Pogo Pads') {
                greenSvgForValue = `<svg viewBox="0 0 165.6 182.99" xmlns="http://www.w3.org/2000/svg"><defs><style>.cls-1{fill:#00b189;}</style></defs><g id="Lock"><g><g id="Pogo_Pins"><circle class="cls-1" cx="58.63" cy="9.81" r="2.89"/><path class="cls-1" d="M54.1,14.16c-1.59,0-2.89,1.3-2.89,2.89s1.3,2.89,2.89,2.89,2.89-1.3,2.89-2.89-1.3-2.89-2.89-2.89Z"/><path class="cls-1" d="M62.86,14.16c-1.59,0-2.89,1.3-2.89,2.89s1.3,2.89,2.89,2.89,2.89-1.3,2.89-2.89-1.3-2.89-2.89-2.89Z"/><path class="cls-1" d="M71.62,14.16c-1.59,0-2.89,1.3-2.89,2.89s1.3,2.89,2.89,2.89,2.89-1.3,2.89-2.89-1.3-2.89-2.89-2.89Z"/><path class="cls-1" d="M80.37,14.16c-1.59,0-2.89,1.3-2.89,2.89s1.3,2.89,2.89,2.89,2.89-1.3,2.89-2.89-1.3-2.89-2.89-2.89Z"/><path class="cls-1" d="M89.13,14.16c-1.59,0-2.89,1.3-2.89,2.89s1.3,2.89,2.89,2.89,2.89-1.3,2.89-2.89-1.3-2.89-2.89-2.89Z"/><path class="cls-1" d="M97.89,14.16c-1.59,0-2.89,1.3-2.89,2.89s1.3,2.89,2.89,2.89,2.89-1.3,2.89-2.89-1.3-2.89-2.89-2.89Z"/><circle class="cls-1" cx="67.42" cy="9.81" r="2.89"/><circle class="cls-1" cx="76.21" cy="9.81" r="2.89"/><circle class="cls-1" cx="85" cy="9.81" r="2.89"/><circle class="cls-1" cx="93.79" cy="9.81" r="2.89"/><path class="cls-1" d="M102.58,6.92c-1.59,0-2.89,1.3-2.89,2.89s1.3,2.89,2.89,2.89,2.89-1.3,2.89-2.89-1.3-2.89-2.89-2.89Z"/></g><path id="D_Shape" class="cls-1" d="M143.19,4.9c.56.03,2.95.37,5.02,1.65,2.43,1.49,3.57,3.68,3.57,6.88s-1.14,5.58-3.59,7.02c-2.32,1.37-4.87,1.5-4.98,1.51H5.57c-.37,0-.67-.3-.67-.67V5.57c0-.37.3-.67.67-.67h137.61M143.22,0H5.57C2.5,0,0,2.5,0,5.57v15.72c0,3.08,2.5,5.57,5.57,5.57h137.65c1.54,0,13.45-1.11,13.45-13.43s-11.92-13.43-13.45-13.43h0Z"/><g id="Trademark"><path class="cls-1" d="M158.21,23.6h-1.53v-.59h3.77v.59h-1.56v3.28h-.68v-3.28ZM161.17,23h.64l1.59,2.03,1.6-2.03h.61v3.87h-.68v-2.78l-1.49,1.81h-.12l-1.47-1.84v2.81h-.68v-3.87Z"/></g></g><g><path class="cls-1" d="M105.52,182.7h-52.32c-.95,0-1.84-.37-2.52-1.04l-37-37c-.67-.67-1.04-1.57-1.04-2.52v-52.32c0-.95.37-1.84,1.04-2.52l37-37c.67-.67,1.57-1.04,2.52-1.04h52.32c.95,0,1.84.37,2.52,1.04l37,37c.67.67,1.04,1.57,1.04,2.52v52.32c0,.95-.37,1.84-1.04,2.52l-37,37c-.67.67-1.57,1.04-2.52,1.04ZM54.42,176.2h49.89l35.28-35.28v-49.89l-35.28-35.28h-49.89l-35.28,35.28v49.89l35.28,35.28Z"/><g><circle class="cls-1" cx="79.23" cy="70.46" r="2.43"/><circle class="cls-1" cx="79.23" cy="76.95" r="2.43"/><circle class="cls-1" cx="79.23" cy="83.44" r="2.43"/><circle class="cls-1" cx="79.23" cy="89.93" r="2.43"/><circle class="cls-1" cx="79.23" cy="96.43" r="2.43"/><circle class="cls-1" cx="79.23" cy="102.92" r="2.43"/><circle class="cls-1" cx="79.23" cy="109.41" r="2.43"/></g><circle class="cls-1" cx="79.3" cy="115.9" r="2.43"/><g><circle class="cls-1" cx="85.85" cy="115.82" r="2.43"/><circle class="cls-1" cx="92.34" cy="115.82" r="2.43"/><circle class="cls-1" cx="98.84" cy="115.82" r="2.43"/><circle class="cls-1" cx="105.33" cy="115.82" r="2.43"/><circle class="cls-1" cx="111.82" cy="115.82" r="2.43"/><circle class="cls-1" cx="118.31" cy="115.82" r="2.43"/><circle class="cls-1" cx="124.8" cy="115.82" r="2.43"/></g><g><circle class="cls-1" cx="79.23" cy="122.39" r="2.43"/><circle class="cls-1" cx="79.23" cy="128.89" r="2.43"/><circle class="cls-1" cx="79.23" cy="135.38" r="2.43"/><circle class="cls-1" cx="79.23" cy="141.87" r="2.43"/><circle class="cls-1" cx="79.23" cy="148.36" r="2.43"/><circle class="cls-1" cx="79.23" cy="154.85" r="2.43"/><circle class="cls-1" cx="79.23" cy="161.35" r="2.43"/></g><g><circle class="cls-1" cx="33.96" cy="115.82" r="2.43"/><circle class="cls-1" cx="40.45" cy="115.82" r="2.43"/><circle class="cls-1" cx="46.94" cy="115.82" r="2.43"/><circle class="cls-1" cx="53.43" cy="115.82" r="2.43"/><circle class="cls-1" cx="59.92" cy="115.82" r="2.43"/><circle class="cls-1" cx="66.42" cy="115.82" r="2.43"/><circle class="cls-1" cx="72.91" cy="115.82" r="2.43"/></g><g id="Trademark-2"><path class="cls-1" d="M129.02,179.19h-1.78v-.69h4.37v.69h-1.81v3.8h-.78v-3.8ZM132.44,178.5h.74l1.84,2.35,1.86-2.35h.7v4.48h-.78v-3.22l-1.73,2.1h-.14l-1.7-2.13v3.25h-.78v-4.48Z"/></g></g></g></svg>`;
                graySvgForValue = `<svg viewBox="0 0 165.6 182.99" xmlns="http://www.w3.org/2000/svg"><defs><style>.st0{fill:#a6a6a6;}</style></defs><g id="Lock"><g><g id="Pogo_Pins"><circle class="st0" cx="58.63" cy="9.81" r="2.89"/><path class="st0" d="M54.1,14.16c-1.59,0-2.89,1.3-2.89,2.89s1.3,2.89,2.89,2.89,2.89-1.3,2.89-2.89-1.3-2.89-2.89-2.89Z"/><path class="st0" d="M62.86,14.16c-1.59,0-2.89,1.3-2.89,2.89s1.3,2.89,2.89,2.89,2.89-1.3,2.89-2.89-1.3-2.89-2.89-2.89Z"/><path class="st0" d="M71.62,14.16c-1.59,0-2.89,1.3-2.89,2.89s1.3,2.89,2.89,2.89,2.89-1.3,2.89-2.89-1.3-2.89-2.89-2.89Z"/><path class="st0" d="M80.37,14.16c-1.59,0-2.89,1.3-2.89,2.89s1.3,2.89,2.89,2.89,2.89-1.3,2.89-2.89-1.3-2.89-2.89-2.89Z"/><path class="st0" d="M89.13,14.16c-1.59,0-2.89,1.3-2.89,2.89s1.3,2.89,2.89,2.89,2.89-1.3,2.89-2.89-1.3-2.89-2.89-2.89Z"/><path class="st0" d="M97.89,14.16c-1.59,0-2.89,1.3-2.89,2.89s1.3,2.89,2.89,2.89,2.89-1.3,2.89-2.89-1.3-2.89-2.89-2.89Z"/><circle class="st0" cx="67.42" cy="9.81" r="2.89"/><circle class="st0" cx="76.21" cy="9.81" r="2.89"/><circle class="st0" cx="85" cy="9.81" r="2.89"/><circle class="st0" cx="93.79" cy="9.81" r="2.89"/><path class="st0" d="M102.58,6.92c-1.59,0-2.89,1.3-2.89,2.89s1.3,2.89,2.89,2.89,2.89-1.3,2.89-2.89-1.3-2.89-2.89-2.89Z"/></g><path id="D_Shape" class="st0" d="M143.19,4.9c.56.03,2.95.37,5.02,1.65,2.43,1.49,3.57,3.68,3.57,6.88s-1.14,5.58-3.59,7.02c-2.32,1.37-4.87,1.5-4.98,1.51H5.57c-.37,0-.67-.3-.67-.67V5.57c0-.37.3-.67.67-.67h137.61M143.22,0H5.57C2.5,0,0,2.5,0,5.57v15.72c0,3.08,2.5,5.57,5.57,5.57h137.65c1.54,0,13.45-1.11,13.45-13.43s-11.92-13.43-13.45-13.43h0Z"/><g id="Trademark"><path class="st0" d="M158.21,23.6h-1.53v-.59h3.77v.59h-1.56v3.28h-.68v-3.28ZM161.17,23h.64l1.59,2.03,1.6-2.03h.61v3.87h-.68v-2.78l-1.49,1.81h-.12l-1.47-1.84v2.81h-.68v-3.87Z"/></g></g><g><path class="st0" d="M105.52,182.7h-52.32c-.95,0-1.84-.37-2.52-1.04l-37-37c-.67-.67-1.04-1.57-1.04-2.52v-52.32c0-.95.37-1.84,1.04-2.52l37-37c.67-.67,1.57-1.04,2.52-1.04h52.32c.95,0,1.84.37,2.52,1.04l37,37c.67.67,1.04,1.57,1.04,2.52v52.32c0,.95-.37,1.84-1.04,2.52l-37,37c-.67.67-1.57,1.04-2.52,1.04ZM54.42,176.2h49.89l35.28-35.28v-49.89l-35.28-35.28h-49.89l-35.28,35.28v49.89l35.28,35.28Z"/><g><circle class="st0" cx="79.23" cy="70.46" r="2.43"/><circle class="st0" cx="79.23" cy="76.95" r="2.43"/><circle class="st0" cx="79.23" cy="83.44" r="2.43"/><circle class="st0" cx="79.23" cy="89.93" r="2.43"/><circle class="st0" cx="79.23" cy="96.43" r="2.43"/><circle class="st0" cx="79.23" cy="102.92" r="2.43"/><circle class="st0" cx="79.23" cy="109.41" r="2.43"/></g><circle class="st0" cx="79.3" cy="115.9" r="2.43"/><g><circle class="st0" cx="85.85" cy="115.82" r="2.43"/><circle class="st0" cx="92.34" cy="115.82" r="2.43"/><circle class="st0" cx="98.84" cy="115.82" r="2.43"/><circle class="st0" cx="105.33" cy="115.82" r="2.43"/><circle class="st0" cx="111.82" cy="115.82" r="2.43"/><circle class="st0" cx="118.31" cy="115.82" r="2.43"/><circle class="st0" cx="124.8" cy="115.82" r="2.43"/></g><g><circle class="st0" cx="79.23" cy="122.39" r="2.43"/><circle class="st0" cx="79.23" cy="128.89" r="2.43"/><circle class="st0" cx="79.23" cy="135.38" r="2.43"/><circle class="st0" cx="79.23" cy="141.87" r="2.43"/><circle class="st0" cx="79.23" cy="148.36" r="2.43"/><circle class="st0" cx="79.23" cy="154.85" r="2.43"/><circle class="st0" cx="79.23" cy="161.35" r="2.43"/></g><g><circle class="st0" cx="33.96" cy="115.82" r="2.43"/><circle class="st0" cx="40.45" cy="115.82" r="2.43"/><circle class="st0" cx="46.94" cy="115.82" r="2.43"/><circle class="st0" cx="53.43" cy="115.82" r="2.43"/><circle class="st0" cx="59.92" cy="115.82" r="2.43"/><circle class="st0" cx="66.42" cy="115.82" r="2.43"/><circle class="st0" cx="72.91" cy="115.82" r="2.43"/></g><g id="Trademark-2"><path class="st0" d="M129.02,179.19h-1.78v-.69h4.37v.69h-1.81v3.8h-.78v-3.8ZM132.44,178.5h.74l1.84,2.35,1.86-2.35h.7v4.48h-.78v-3.22l-1.73,2.1h-.14l-1.7-2.13v3.25h-.78v-4.48Z"/></g></g></g></svg>`;
            }

            return {
                displayType: 'Icon',
                svgContent: isSelected ? greenSvgForValue : graySvgForValue,
                svgs: {
                    selected: greenSvgForValue,
                    unselected: graySvgForValue
                }
            };
        }

        if (field === 'Lock Type' || field === 'Universal Holder') {
            let greenSvgForValue = '';
            let graySvgForValue = '';

            if (value === 'Left Locking' || value === 'Left Locking (Selected)' || value === 'Left Locking (Not Selected)') {
                greenSvgForValue = `<svg viewBox="0 0 118.15 85.91" xmlns="http://www.w3.org/2000/svg"><defs><style>.cls-1{fill:#00b189;}</style></defs><g id="Lock"><g><path class="cls-1" d="M52.8,33.21h9.49c-5.96-44.29,52.33-44.27,46.36,0h9.49s0,52.69,0,52.69H52.8v-52.7ZM80.97,7.1c-10.1,2.1-14.44,16.08-11.9,25.12l32.28.53c4.43-13.86-4.47-28.95-20.38-25.65ZM111.82,39.53h-52.7v39h52.7v-39Z"/><path class="cls-1" d="M9.77,78.1h25.53v7.8H0V29.67h9.77v48.44Z"/></g></g></svg>`;
                graySvgForValue = `<svg viewBox="0 0 118.2 85.9" xmlns="http://www.w3.org/2000/svg"><defs><style>.st0{fill:#a6a6a6;}</style></defs><g id="Lock"><g><path class="st0" d="M52.8,33.2h9.5c-6-44.3,52.3-44.3,46.4,0h9.5v52.7H52.8v-52.7h0ZM81,7.1c-10.1,2.1-14.4,16.1-11.9,25.1l32.3.5c4.4-13.9-4.5-29-20.4-25.7ZM111.8,39.5h-52.7v39h52.7v-39Z"/><path class="st0" d="M9.8,78.1h25.5v7.8H0V29.7h9.8v48.4Z"/></g></g></svg>`;
            } else if (value === 'Right Locking' || value === 'Right Locking (Selected)' || value === 'Right Locking (Not Selected)') {
                greenSvgForValue = `<svg viewBox="0 0 122.16 85.91" xmlns="http://www.w3.org/2000/svg"><defs><style>.cls-1{fill:#00b189;}</style></defs><g id="Lock"><g><path class="cls-1" d="M0,33.21h9.49c-5.96-44.29,52.33-44.27,46.36,0h9.49s0,52.69,0,52.69H0v-52.7ZM28.17,7.1c-10.1,2.1-14.44,16.08-11.9,25.12l32.28.53c4.43-13.86-4.47-28.95-20.38-25.65ZM59.02,39.53H6.32v39h52.7v-39Z"/><path class="cls-1" d="M100.41,64.28h-10.89v21.63h-9.77V29.67h19.78c6.49,0,11.5,1.46,15.03,4.37,3.53,2.91,5.29,7.13,5.29,12.64,0,3.76-.91,6.91-2.72,9.45s-4.35,4.49-7.59,5.86l12.63,23.42v.5h-10.47l-11.28-21.63ZM89.52,56.4h10.04c3.3,0,5.87-.83,7.72-2.49,1.85-1.66,2.78-3.93,2.78-6.82s-.86-5.34-2.57-6.99c-1.71-1.65-4.27-2.5-7.67-2.55h-10.31v18.85Z"/></g></g></svg>`;
                graySvgForValue = `<svg viewBox="0 0 122.2 85.9" xmlns="http://www.w3.org/2000/svg"><defs><style>.st0{fill:#a6a6a6;}</style></defs><g id="Lock"><g><path class="st0" d="M0,33.2h9.5c-6-44.3,52.3-44.3,46.4,0h9.5v52.7H0v-52.7h0ZM28.2,7.1c-10.1,2.1-14.4,16.1-11.9,25.1l32.3.5c4.4-13.9-4.5-29-20.4-25.7ZM59,39.5H6.3v39h52.7v-39Z"/><path class="st0" d="M100.4,64.3h-10.9v21.6h-9.8V29.7h19.8c6.5,0,11.5,1.5,15,4.4s5.3,7.1,5.3,12.6-.9,6.9-2.7,9.5c-1.8,2.5-4.3,4.5-7.6,5.9l12.6,23.4v.5h-10.5l-11.3-21.6h0ZM89.5,56.4h10c3.3,0,5.9-.8,7.7-2.5s2.8-3.9,2.8-6.8-.9-5.3-2.6-7-4.3-2.5-7.7-2.5h-10.3v18.9h0Z"/></g></g></svg>`;
            } else if (value === 'Locking' || value === 'Locking (Selected)' || value === 'Locking (Not Selected)') {
                greenSvgForValue = `<svg viewBox="0 0 65.35 85.91" xmlns="http://www.w3.org/2000/svg"><defs><style>.cls-1{fill:#00b189;}</style></defs><g id="Lock"><path class="cls-1" d="M0,33.21h9.49c-5.96-44.29,52.33-44.27,46.36,0h9.49s0,52.69,0,52.69H0v-52.7ZM28.17,7.1c-10.1,2.1-14.44,16.08-11.9,25.12l32.28.53c4.43-13.86-4.47-28.95-20.38-25.65ZM59.02,39.53H6.32v39h52.7v-39Z"/></g></svg>`;
                graySvgForValue = `<svg viewBox="0 0 65.3 85.9" xmlns="http://www.w3.org/2000/svg"><defs><style>.st0{fill:#a6a6a6;}</style></defs><g id="Lock"><path class="st0" d="M0,33.2h9.5c-6-44.3,52.3-44.3,46.4,0h9.5v52.7H0v-52.7h0ZM28.2,7.1c-10.1,2.1-14.4,16.1-11.9,25.1l32.3.5c4.4-13.9-4.5-29-20.4-25.7ZM59,39.5H6.3v39h52.7v-39Z"/></g></svg>`;
            } else if (value === 'Non-Locking' || value === 'Non-Locking (Selected)' || value === 'Non-Locking (Not Selected)') {
                greenSvgForValue = `<svg viewBox="0 0 80.26 80.26" xmlns="http://www.w3.org/2000/svg"><defs><style>.cls-1{fill:#00b189;}</style></defs><g id="Unavailable"><path class="cls-1" d="M40.13,0C18,0,0,18,0,40.13s18,40.13,40.13,40.13,40.13-18,40.13-40.13S62.26,0,40.13,0ZM40.13,7c7.8,0,14.97,2.71,20.63,7.24L14.05,60.52c-4.41-5.63-7.05-12.71-7.05-20.39C7,21.86,21.86,7,40.13,7ZM40.13,73.26c-8.06,0-15.46-2.9-21.21-7.7l46.83-46.4c4.69,5.72,7.51,13.02,7.51,20.98,0,18.27-14.86,33.13-33.13,33.13Z"/></g></svg>`;
                graySvgForValue = `<svg viewBox="0 0 80.3 80.3" xmlns="http://www.w3.org/2000/svg"><defs><style>.st0{fill:#a6a6a6;}</style></defs><g id="Unavailable"><path class="st0" d="M40.1,0C18,0,0,18,0,40.1s18,40.1,40.1,40.1,40.1-18,40.1-40.1S62.3,0,40.1,0ZM40.1,7c7.8,0,15,2.7,20.6,7.2L14.1,60.5c-4.4-5.6-7.1-12.7-7.1-20.4C7,21.9,21.9,7,40.1,7ZM40.1,73.3c-8.1,0-15.5-2.9-21.2-7.7l46.8-46.4c4.7,5.7,7.5,13,7.5,21,0,18.3-14.9,33.1-33.1,33.1h0Z"/></g></svg>`;
            }
            return {
                displayType: 'Icon',
                svgContent: isSelected ? greenSvgForValue : graySvgForValue, // Fallback
                svgs: {
                    selected: greenSvgForValue,
                    unselected: graySvgForValue
                }
            };
        }
        return defaultDisplay;
    }

    createHTML(product, cardTitle, cardData, modalId, showEditButton = true) {
        if (!showEditButton) {
            return '';
        }

        return `
            <div id="${modalId}" class="fixed inset-0 z-50 hidden product-modal">
                <div class="modal-backdrop absolute inset-0 bg-black bg-opacity-50 transition-opacity duration-300" style="opacity: 0;"></div>

                <!-- Desktop Modal -->
                <div class="hidden md:flex items-center justify-center min-h-screen p-4">
                    <div class="desktop-modal-content relative bg-white w-full max-w-md mx-auto rounded-lg shadow-xl transform transition-all duration-300" style="min-width: 16rem; transform: scale(0.95);">
                        <div class="flex items-center justify-between p-4">
                            <h3 class="text-lg font-semibold text-green">Customize</h3>
                            <button type="button" class="modal-close text-gray transition-transform duration-300 hover:scale-110 cursor-pointer">
                                <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div class="px-6">
                            <div class="space-y-4 mb-6 modal-options-container">
                                <p class="text-sm text-gray-500 italic">Loading options...</p>
                            </div>
                        </div>
                        <div class="px-6 py-4 flex justify-center space-x-3">
                            <div class="flex flex-col items-center justify-center">
                                <button type="button" class="modal-save-btn flex items-center px-6 py-2 btn-green text-white rounded-md btn-green:hover transition-colors font-medium">
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Mobile Modal -->
                <div class="block md:hidden">
                    <div class="mobile-modal-content fixed inset-x-0 bottom-0 transform transition-transform duration-300" style="transform: translateY(100%);">
                        <div class="bg-white rounded-t-3xl shadow-xl w-full">
                            <div class="w-full py-3 flex justify-center">
                                <div class="w-12 h-1 bg-gray-300 rounded-full"></div>
                            </div>
                            <div class="px-4 py-2 flex items-center">
                                <h3 class="text-xl font-semibold text-green">Customize</h3>
                            </div>
                            <div class="p-5 max-h-[85vh] overflow-y-auto">
                                <div class="space-y-4 mb-6 modal-options-container">
                                    <p class="text-sm text-gray-500 italic">Loading options...</p>
                                </div>
                            </div>
                            <div class="pb-12 w-full flex justify-center space-x-3">
                                <div class="mt-2 flex flex-col items-center justify-center">
                                    <button type="button" class="modal-save-btn w-full flex items-center justify-center px-6 py-3 btn-green text-white rounded-md btn-green:hover transition-colors font-medium">
                                        Confirm
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }
}
